/**
 * SessionInfoPanel - Floating right-side details panel for session info
 *
 * Slides in from the right edge of the chat area, overlaying the content.
 * Shows session title (editable) and files section.
 * Triggered by the sidebar toggle button in the PanelHeader.
 */

import * as React from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useAppShellContext, useSession } from '@/context/AppShellContext'
import { useResizeGradient } from '@/hooks/useResizeGradient'
import * as storage from '@/lib/local-storage'
import { PANEL_SASH_HIT_WIDTH, PANEL_SASH_LINE_WIDTH } from './panel-constants'
import { SessionFilesSection } from '../right-sidebar/SessionFilesSection'
import { PlanProgressSection } from '../right-sidebar/PlanProgressSection'

const DEFAULT_DETAILS_WIDTH = 320
const MIN_DETAILS_WIDTH = 260
const MAX_DETAILS_WIDTH = 720

function clampDetailsWidth(width: number): number {
  return Math.min(MAX_DETAILS_WIDTH, Math.max(MIN_DETAILS_WIDTH, Math.round(width)))
}

interface SessionInfoPanelProps {
  sessionId: string
  open: boolean
  onClose: () => void
}

export function SessionInfoPanel({ sessionId, open, onClose }: SessionInfoPanelProps) {
  const session = useSession(sessionId)
  const { onRenameSession } = useAppShellContext()
  const [name, setName] = React.useState('')
  const renameTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const [width, setWidth] = React.useState(() =>
    clampDetailsWidth(storage.get(storage.KEYS.sessionDetailsWidth, DEFAULT_DETAILS_WIDTH)),
  )
  const widthRef = React.useRef(width)
  widthRef.current = width
  const stopResizeRef = React.useRef<(() => void) | null>(null)
  const { ref: sashRef, handlers: sashHandlers, gradientStyle } = useResizeGradient()

  const persistWidth = React.useCallback((nextWidth: number) => {
    const clamped = clampDetailsWidth(nextWidth)
    setWidth(clamped)
    storage.set(storage.KEYS.sessionDetailsWidth, clamped)
  }, [])

  const stopResize = React.useCallback((shouldPersist: boolean) => {
    const cleanup = stopResizeRef.current
    stopResizeRef.current = null
    cleanup?.()
    document.body.style.userSelect = ''
    document.body.style.cursor = ''
    if (shouldPersist) persistWidth(widthRef.current)
  }, [persistWidth])

  const handleResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    stopResize(false)
    sashHandlers.onMouseDown()
    const startX = e.clientX
    const startWidth = widthRef.current

    const onMove = (ev: MouseEvent) => {
      setWidth(clampDetailsWidth(startWidth + (startX - ev.clientX)))
    }
    const onUp = () => stopResize(true)

    stopResizeRef.current = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.body.style.userSelect = 'none'
    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [stopResize, sashHandlers])

  React.useEffect(() => {
    setName(session?.name || '')
  }, [session?.name])

  React.useEffect(() => {
    const onBlur = () => stopResize(true)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('blur', onBlur)
      if (renameTimeoutRef.current) {
        clearTimeout(renameTimeoutRef.current)
      }
      stopResize(false)
    }
  }, [stopResize])

  const handleNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value
    setName(newName)

    if (renameTimeoutRef.current) {
      clearTimeout(renameTimeoutRef.current)
    }

    renameTimeoutRef.current = setTimeout(() => {
      const trimmed = newName.trim()
      if (trimmed) {
        onRenameSession(sessionId, trimmed)
      }
    }, 500)
  }, [onRenameSession, sessionId])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 16 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className="absolute top-[50px] right-2 bottom-2 z-30 flex flex-col rounded-xl bg-background shadow-modal-small border border-border/40 overflow-hidden"
          style={{ width }}
        >
          <div
            ref={sashRef}
            className="absolute inset-y-0 left-0 z-10 flex justify-center cursor-col-resize"
            style={{ width: PANEL_SASH_HIT_WIDTH }}
            title="Drag to resize"
            onMouseDown={handleResizeStart}
            onMouseMove={sashHandlers.onMouseMove}
            onMouseLeave={sashHandlers.onMouseLeave}
            onDoubleClick={() => persistWidth(DEFAULT_DETAILS_WIDTH)}
          >
            <div
              className="h-full"
              style={{ ...gradientStyle, width: PANEL_SASH_LINE_WIDTH }}
            />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-3.5 py-2.5 shrink-0">
            <span className="text-sm font-semibold select-none">Details</span>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded-md hover:bg-foreground/5 transition-colors opacity-60 hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Title field */}
          <div className="shrink-0 px-3.5 pb-3 border-b border-border/50">
            <label className="text-xs font-medium text-muted-foreground block mb-1.5 select-none">
              Title
            </label>
            <div className="rounded-lg bg-foreground-2 has-[:focus]:bg-background shadow-minimal transition-colors">
              <Input
                value={name}
                onChange={handleNameChange}
                placeholder="Untitled"
                className="h-9 py-2 text-sm border-0 shadow-none bg-transparent focus-visible:ring-0"
              />
            </div>
          </div>

          {/* Plan Progress */}
          <PlanProgressSection sessionId={sessionId} />

          {/* Files */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <SessionFilesSection
              sessionId={sessionId}
              hideHeader={false}
              className="h-full min-h-0"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
